import React from 'react';
import { Icon } from '@iconify/react';
import { formatIntDateTime } from '../utils/dateUtils.js'; 

const ShiftLogDataTable = ({
  logs = [],
  sortField,
  sortOrder,
  onSort,
  onOpenShiftLog,
  onViewShiftLog,
  onEditShiftLog,
  onDeleteShiftLog,
  loading,
  timeZone,
  activeTab = 'inProgress',
  permissions = {},
}) => {
  const { canView = false, canUpdate = false, canDelete = false } = permissions;

  const isCompletedTab = activeTab === 'completed';
  const showInProgressActions = activeTab === 'inProgress' && (canUpdate || canDelete);
  const showCompletedActions = isCompletedTab && (canView || canDelete);
  const showActionsColumn = showInProgressActions || showCompletedActions;
  const totalColumns = 5 + (isCompletedTab ? 1 : 0) + (showActionsColumn ? 1 : 0);

  const getSortIcon = (field) => {
    if (sortField !== field || !sortOrder) {
      return 'mdi:sort';
    }
    return sortOrder === 'asc' ? 'mdi:sort-ascending' : 'mdi:sort-descending';
  };

  const renderSortableHeader = (label, field) => {
    const ariaSort =
      sortField === field
        ? sortOrder === 'asc'
          ? 'ascending'
          : sortOrder === 'desc'
            ? 'descending'
            : 'none'
        : 'none';

    return (
      <th
        scope="col"
        onClick={() => onSort?.(field)}
        style={{ cursor: 'pointer' }}
        aria-sort={ariaSort}
      >
        <span className="d-inline-flex align-items-center gap-1">
          {label}
          <Icon icon={getSortIcon(field)} className="text-xl" />
        </span>
      </th>
    );
  };

  return (
    <table className="table bordered-table mb-0 w-100 rounded-0">
      <thead>
        <tr>
          {renderSortableHeader('Department', 'department')}
          {renderSortableHeader('Supervisor', 'user')}
          {renderSortableHeader('Shift Name', 'shiftName')}
          {renderSortableHeader('Report Start', 'reportStart')}
          {renderSortableHeader('Report End', 'reportEnd')}
          {isCompletedTab && <th scope="col" className="text-center">Report</th>}
          {showActionsColumn && <th scope="col">Actions</th>}
        </tr>
      </thead>
      <tbody>
        {logs.length === 0 && !loading && (
          <tr>
            <td colSpan={totalColumns} className="text-center py-4">
              No shift logs found.
            </td>
          </tr>
        )}
        {logs.map((log) => (
          <tr key={log.id}>
            <td>
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none"
                onClick={() => onOpenShiftLog?.(log)}
              >
                {log.departmentName || `Department ${log.departmentId ?? ''}`}
              </button>
            </td>
            <td>{log.userName || (log.userId ? `User ${log.userId}` : '—')}</td>
            <td>{log.shiftName || '—'}</td>
            <td>{formatIntDateTime(log.reportStartDate, timeZone)}</td>
            <td>{formatIntDateTime(log.reportEndDate, timeZone)}</td>
            {isCompletedTab && (
              <td className="text-center">
                {log.reportUrl ? (
                  <a
                    href={log.reportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open shift log report"
                    className="text-danger"
                  >
                    <Icon icon="file-icons:adobe-acrobat" className="text-xl" />
                  </a>
                ) : (
                  '—'
                )}
              </td>
            )}
            {showActionsColumn && (
              <td>
                {activeTab === 'inProgress' && canUpdate && (
                  <button
                    type="button"
                    className="edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                    onClick={() => onEditShiftLog?.(log)}
                  >
                    <Icon icon="ic:baseline-edit" />
                  </button>
                )}
                {activeTab === 'inProgress' && canDelete && (
                  <button
                    type="button"
                    className={`delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center ${canUpdate ? 'ms-6' : ''}`}
                    onClick={() => onDeleteShiftLog?.(log)}
                  >
                    <Icon icon="ic:baseline-delete-outline" />
                  </button>
                )}
                {isCompletedTab && canView && (
                  <button
                    type="button"
                    className="edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                    onClick={() => onViewShiftLog?.(log)}
                  >
                    <Icon icon="ic:baseline-edit" />
                  </button>
                )}
                {isCompletedTab && canDelete && (
                  <button
                    type="button"
                    className={`delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center ${isCompletedTab && canView ? 'ms-6' : ''}`}
                    onClick={() => onDeleteShiftLog?.(log)}
                  >
                    <Icon icon="ic:baseline-delete-outline" />
                  </button>
                )}
              </td>
            )}
          </tr>
        ))}
        {loading && (
          <tr>
            <td colSpan={totalColumns} className="text-center py-3">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default ShiftLogDataTable;
